import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, setLocale, useAntdConfigSetter, useIntl } from '@umijs/max';
import { AiChat } from '@zhiyue/ai-chat';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import PlatformTheme from '@/components/PlatformTheme';
import { getLayoutThemeToken } from '@/components/PlatformTheme/layoutTokens';
import '@zhiyue/core/style.css';

import { HomeOutlined } from '@ant-design/icons';
import { theme as antdTheme } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import {
  getBootstrapTenantList,
  sessionAdapter,
} from '@/adapters/sessionAdapter';
import {
  AvatarDropdown,
  NotificationIcon,
  ParkSwitcher,
  Question,
  SelectLang,
  TenantSwitcher,
  ThemeToggle,
} from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import {
  EnterpriseBootstrapProvider,
  getEnterpriseBootstrapSnapshot,
  subscribeEnterpriseBootstrap,
} from '@/enterprise/bootstrap';
import { EnterpriseRouteGate } from '@/enterprise/routeGate';
import { ParkContextProvider } from '@/park/context';
import { ParkScopeGate } from '@/park/ParkScopeGate';
import { shouldShowEnterpriseSider } from '@/park/scope';
import {
  type AuthorizedResourceAccessMap,
  buildAuthorizedResourceAccessMap,
} from '@/permissions/adapter';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  getAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import defaultSettings from '../config/defaultSettings';
import { readCurrentTenantUuid } from './adapters/tenantStorage';
import {
  errorConfig,
  getRequestOrganizationUuid,
  getRequestTenantUuid,
} from './requestErrorConfig';
import { getMyAuthorizedResources } from './services/bff/permission.api';

export const request: RequestConfig = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback'];
const getUserSyncKey = (
  user?: (Partial<CurrentUser> & { uuid?: string }) | null,
) =>
  user
    ? [user.id, user.uuid, user.username, user.tenantUuid]
        .filter(Boolean)
        .join('|')
    : '';

const getSessionConfig = (): CoreSessionConfig => ({
  redirectAfterLogout: loginPath,
  adapter: sessionAdapter,
  ui: {
    links: { help: 'https://docs.shanhuyun.cn/' },
    userMenu: { items: [] },
    locales: [
      { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
      { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
      { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
      { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
    ],
  },
  featureFlags: {
    showHeader: true,
    showNav: true,
    showFooter: true,
    enableTenantSwitch: true,
    enableRegister: false,
  },
  onTenantSwitched: () => {
    const returnTo = consumeApplicationAccessReturnTo();
    window.location.href = returnTo || '/workbench';
  },
  onLocaleChanged: (locale) => setLocale(locale, false),
  onUnauthorized: () => {
    if (!publicPaths.includes(history.location.pathname)) redirectToSSO();
  },
});

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const { navTheme } = useTheme();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const syncedThemeRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({ navTheme: '', userSyncKey: '' });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });
  }, [currentUser, loading, shellConfig.featureFlags]);

  React.useEffect(() => {
    if (typeof setInitialState !== 'function') {
      return;
    }

    const previous = syncedRef.current;
    if (
      previous.navTheme === navTheme &&
      previous.userSyncKey === userSyncKey
    ) {
      return;
    }
    setInitialState((state: any) => {
      if (!state) return state;
      return {
        ...state,
        currentUser,
        settings: { ...state.settings, navTheme },
      };
    });
    syncedRef.current = { navTheme, userSyncKey };
  }, [currentUser, navTheme, setInitialState, userSyncKey]);

  React.useEffect(() => {
    if (loading) return;
    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }
    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (syncedThemeRef.current === navTheme) return;
    syncedThemeRef.current = navTheme;
    setAntdConfig((previous) => ({
      ...previous,
      theme: {
        ...previous?.theme,
        algorithm:
          navTheme === 'realDark'
            ? antdTheme.darkAlgorithm
            : antdTheme.defaultAlgorithm,
      },
    }));
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    const syncLocale = () => setRuntimeLocale(getStoredBrandLocale());
    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);
    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const title = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((state: any) =>
      state ? { ...state, settings: { ...state.settings, title } } : state,
    );
  }, [runtimeLocale, setInitialState]);

  return null;
};

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
});

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  workbenchUrl?: string;
}> = ({ logo, appTitle, workbenchUrl }) => {
  const intl = useIntl();
  const workbenchLabel = intl.formatMessage({ id: 'menu.account.workbench' });

  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button>
      <button
        type="button"
        className="app-header-workbench-btn"
        onClick={() => {
          if (!workbenchUrl) {
            return;
          }
          window.open(workbenchUrl, '_self');
        }}
        disabled={!workbenchUrl}
        title={workbenchLabel}
      >
        <HomeOutlined />
        <span className="app-header-workbench-text">{workbenchLabel}</span>
      </button>
    </div>
  );
};

export const rootContainer = (
  container: React.ReactNode,
  {
    setInitialState,
  }: { initialState: any; setInitialState: (updater: any) => void },
) => (
  <RootSessionContainer setInitialState={setInitialState}>
    {container}
  </RootSessionContainer>
);

const RootSessionContainer: React.FC<{
  setInitialState: (updater: any) => void;
  children: React.ReactNode;
}> = ({ children, setInitialState }) => {
  const sessionConfig = React.useMemo(() => {
    if (
      typeof window !== 'undefined' &&
      localStorage.getItem('app_nav_theme') === null
    ) {
      localStorage.setItem('app_nav_theme', 'realDark');
    }
    return getSessionConfig();
  }, []);
  return (
    <SessionProvider config={sessionConfig}>
      <PlatformTheme staticMessages>
        <SessionSync setInitialState={setInitialState} />
        <EnterpriseBootstrapProvider setInitialState={setInitialState}>
          <ParkContextProvider>
            <EnterpriseRouteGate>{children}</EnterpriseRouteGate>
          </ParkContextProvider>
        </EnterpriseBootstrapProvider>
        <FloatingAiChat />
      </PlatformTheme>
    </SessionProvider>
  );
};

const FloatingAiChat: React.FC = () => {
  const { currentUser, loading } = useSession();
  const { navTheme } = useTheme();
  if (
    loading ||
    !currentUser ||
    publicPaths.includes(history.location.pathname)
  )
    return null;
  return (
    <AiChat
      title="AI 助手"
      position="bottom-right"
      theme={navTheme === 'realDark' ? 'dark' : 'light'}
      authHeaders={() => {
        const headers: Record<string, string> = {};
        const token = getAccessToken();
        const tenantUuid = getRequestTenantUuid();
        const organizationUuid = getRequestOrganizationUuid();
        if (token) headers.Authorization = `Bearer ${token}`;
        if (tenantUuid) headers['x-tenant-id'] = tenantUuid;
        if (organizationUuid) headers['x-organization-id'] = organizationUuid;
        return headers;
      }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  enterpriseBootstrap?: {
    state?: import('@/services/bff/types/onboarding').EnterpriseBootstrapState;
    loading: boolean;
  };
  loading?: boolean;
}> {
  let authorizedResourceAccess: AuthorizedResourceAccessMap = {};
  let currentTenantIsAdmin = false;

  // callback 页面里会在 token 落库后主动 refresh 初始态；
  // 这里不能再排除 /callback，否则首登回跳后权限态会一直是空的。
  if (hasOAuth2Session()) {
    const [authorizedResourcesResult, tenantListResult] =
      await Promise.allSettled([
        getMyAuthorizedResources(BizAppConfig.appSsoClientId || ''),
        getBootstrapTenantList(),
      ]);
    if (authorizedResourcesResult.status === 'fulfilled') {
      authorizedResourceAccess = buildAuthorizedResourceAccessMap(
        authorizedResourcesResult.value,
      );
    }

    if (tenantListResult.status === 'fulfilled') {
      const tenantList = tenantListResult.value ?? [];
      const currentTenantUuid = readCurrentTenantUuid() || tenantList[0]?.id;
      currentTenantIsAdmin = tenantList.some(
        (tenant) =>
          tenant.id === currentTenantUuid && tenant.roleLabel === '管理员',
      );
    }
  }
  return {
    loading: true,
    authorizedResourceAccess,
    currentTenantIsAdmin,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const [, setBootstrapVersion] = React.useState(0);
  React.useEffect(
    () =>
      subscribeEnterpriseBootstrap(() =>
        setBootstrapVersion((version) => version + 1),
      ),
    [],
  );
  const getCurrentLayoutVisibility = () => {
    const visibility = getLayoutVisibility(history.location.pathname);
    return {
      ...visibility,
      showNav:
        visibility.showNav &&
        shouldShowEnterpriseSider(getEnterpriseBootstrapSnapshot().data?.state),
    };
  };
  const { showNav } = getCurrentLayoutVisibility();
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;
  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;
  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((state) => {
        if (!state) return state;
        const settings =
          (state.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (settings.siderWidth === nextWidth) return state;
        return {
          ...state,
          settings: { ...settings, siderWidth: nextWidth },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    menu: defaultSettings.menu,
    navTheme,
    className: showNav ? undefined : 'enterprise-layout-no-sider',
    token: getLayoutThemeToken(navTheme),
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <TenantSwitcher key="current-tenant" />,
        <ParkSwitcher key="park-switcher" />,
        <NotificationIcon key="notification" />,
        <Question key="doc" />,
        <ThemeToggle key="themeToggle" />,
        <SelectLang key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return null;
    },
    breadcrumbRender: false,
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: showNav ? undefined : false,
    menuDataRender: (menuData) => (showNav ? menuData : []),
    collapsedButtonRender: showNav ? undefined : false,
    headerTitleRender: (logo) => (
      <HeaderTitle
        logo={logo}
        appTitle={appTitle}
        workbenchUrl={workbenchUrl}
      />
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    siderWidth: showNav ? siderWidth : 0,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          <PlatformTheme>
            <ParkScopeGate>{children}</ParkScopeGate>
          </PlatformTheme>
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((state) => ({ ...state, settings }));
              }}
            />
          )}
        </>
      );
    },
  };
};
